import { AlertTriangle, Clock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useWorklogSummary } from '@/features/projects/queries';
import { formatMinutes } from '@/features/projects/timeFormat';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { isIsoDay } from '@/lib/datetime';
import { useStaffInvoiceEmployee } from '../queries';

/** The employee's logged time for the invoice period (DEV13-77) — total plus a
 *  per-project split from the server-side worklog summary (ADR-099), so the
 *  accountant can bill hours that match the timesheets. All logged time counts
 *  (staff are paid for non-billable work too). The accountant may still type
 *  other hours; a mismatch is flagged, not blocked. */
export function StaffInvoiceLoggedHours({
  employeeId,
  periodStart,
  periodEnd,
  enteredMinutes,
  onUseLogged,
}: {
  employeeId: number;
  periodStart: string;
  periodEnd: string;
  /** The hours currently in the form, in minutes. */
  enteredMinutes: number;
  onUseLogged: (minutes: number) => void;
}) {
  const employee = useStaffInvoiceEmployee(employeeId);
  const userId = employee.data?.user ?? null;
  // Date inputs emit partial years while typing — wait for a settled, valid range.
  const from = useDebouncedValue(periodStart);
  const to = useDebouncedValue(periodEnd);
  const validRange = isIsoDay(from) && isIsoDay(to) && from <= to;
  const summary = useWorklogSummary(
    { user: userId ?? undefined, date_from: from, date_to: to, group_by: 'project' },
    Boolean(employeeId && userId && validRange),
  );

  if (!employeeId || !periodStart || !periodEnd) return null;
  if (employee.isError) {
    return <p className="text-destructive text-sm">{"Couldn't load this employee's worklogs."}</p>;
  }
  if (employee.data && !userId) {
    return (
      <p className="text-muted-foreground text-sm">
        This employee has no login, so their worklogs cannot be linked — enter the hours manually.
      </p>
    );
  }
  // Until the employee's user is known the query has no `user` key — the same
  // key as an org-wide summary another page may have cached (everyone's time).
  if (!userId) return null;
  if (summary.isError) {
    return <p className="text-destructive text-sm">{"Couldn't load the logged time for this period."}</p>;
  }
  // Never show (or let the button apply) a previous employee's or period's
  // total while the new one loads — keepPreviousData would otherwise do so.
  const data = summary.data;
  const settled = from === periodStart && to === periodEnd; // not mid-debounce
  if (!validRange || !settled || !data || summary.isPlaceholderData || data.group_by !== 'project') {
    return null;
  }

  const logged = data.total_minutes;
  const mismatch = enteredMinutes > 0 && enteredMinutes !== logged;

  return (
    <section aria-label="Logged time" className="space-y-2 rounded-md border p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5">
          <Clock className="text-muted-foreground size-4" />
          Logged in this period:{' '}
          <span className="font-medium tabular-nums">{formatMinutes(logged)}</span>
          {data.groups.length > 1 ? (
            <span className="text-muted-foreground">across {data.groups.length} projects</span>
          ) : null}
        </p>
        {logged > 0 && enteredMinutes !== logged ? (
          <Button type="button" size="sm" variant="outline" onClick={() => onUseLogged(logged)}>
            Use logged hours
          </Button>
        ) : null}
      </div>
      {data.groups.length > 0 ? (
        <ul className="text-muted-foreground space-y-0.5">
          {data.groups.map((g) => (
            <li key={String(g.key)} className="flex justify-between gap-4">
              <span className="truncate">{g.label}</span>
              <span className="tabular-nums">{formatMinutes(g.minutes)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground">No worklogs in this period.</p>
      )}
      {mismatch ? (
        <p role="status" className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
          <AlertTriangle className="size-4" />
          The hours entered ({formatMinutes(enteredMinutes)}) differ from the logged time (
          {formatMinutes(logged)}).
        </p>
      ) : null}
    </section>
  );
}
